// Staking configuration for LRS, LRSN, and SILO tokens

export interface StakingPeriod {
  days: number;
  monthlyRate: number; // Percentage per month
  apy: number; // Annual Percentage Yield
  label: string;
  bonusRate?: number; // Bonus percentage at maturity (optional)
  bonusMinimumInvest?: bigint; // Minimum investment to get bonus (optional, in wei)
}

export interface StakingOption {
  id: string;
  tokenSymbol: string;
  tokenName: string;
  tokenColor: string;
  minStake: number;
  maxStake?: number; // undefined means no maximum
  rewardDistribution: string;
  rewardToken: string; // Token in which rewards are paid
  isFlexible: boolean; // true for flexible staking, false for time-locked
  periods?: StakingPeriod[]; // Only for time-locked staking (LRSN)
  apy?: number; // Only for flexible staking (LRS, SILO)
  earlyWithdrawal?: {
    allowed: boolean;
    penalty?: number; // Percentage penalty
  };
  contractAddress?: string; // Staking contract address
  tokenAddress?: string; // Token contract address (for ERC20 tokens)
  description: string;
}

export const stakingOptions: StakingOption[] = [
  {
    id: "lrs-staking",
    tokenSymbol: "LRS",
    tokenName: "Larissa",
    tokenColor: "#FF8A3C",
    minStake: 100000,
    maxStake: 100000,
    rewardDistribution: "Every Monday",
    rewardToken: "LRS",
    isFlexible: true,
    apy: 25, // 25% APY - placeholder, update with actual rate
    contractAddress: import.meta.env.VITE_LRS_STAKING_CONTRACT_ADDRESS || '', // LRS Staking Contract
    tokenAddress: import.meta.env.VITE_WLRS_TOKEN_ADDRESS || '', // WLRS Token
    description: "Flexible staking with weekly rewards. No lock period required.",
  },
  {
    id: "lrsn-staking",
    tokenSymbol: "LRSN",
    tokenName: "Larissa Network Token",
    tokenColor: "#8B5CF6",
    minStake: 1, // No minimum from contract
    maxStake: undefined,
    rewardDistribution: "Monthly + Bonus at Maturity",
    rewardToken: "LRSN",
    isFlexible: false,
    periods: [
      {
        days: 30,
        monthlyRate: 2.5, // 250 basis points = 2.5%
        apy: 30,
        label: "30 Days",
      },
      {
        days: 90,
        monthlyRate: 2.8, // 280 basis points = 2.8%
        apy: 33.6,
        label: "90 Days",
      },
      {
        days: 180,
        monthlyRate: 3.0, // 300 basis points = 3.0%
        apy: 36,
        label: "180 Days",
      },
      {
        days: 365,
        monthlyRate: 3.33, // 333 basis points = 3.33%
        apy: 39.96,
        label: "365 Days",
      },
      {
        days: 730,
        monthlyRate: 4.16, // 416 basis points = 4.16%
        apy: 49.92,
        label: "730 Days",
      },
    ],
    earlyWithdrawal: {
      allowed: false,
      penalty: 0,
    },
    description: "Time-locked staking with monthly interest claims and bonus rewards at maturity.",
    contractAddress: import.meta.env.VITE_LRSN_STAKING_CONTRACT_ADDRESS || '',
    tokenAddress: import.meta.env.VITE_LRSN_TOKEN_ADDRESS || '',
  },
];

// Helper function to format large numbers
export function formatStakeAmount(amount: number): string {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(0)}K`;
  }
  return amount.toString();
}

// Helper function to calculate projected rewards
export function calculateRewards(
  amount: number,
  option: StakingOption,
  period?: StakingPeriod
): number {
  if (option.isFlexible && option.apy) {
    // For flexible staking, calculate weekly rewards (assuming 52 weeks per year)
    return (amount * option.apy) / 100 / 52;
  } else if (!option.isFlexible && period) {
    // For time-locked staking, calculate total rewards at maturity
    const months = period.days / 30;
    return (amount * period.monthlyRate * months) / 100;
  }
  return 0;
}

